import { useEffect, useState, useRef } from "react";
import { IoClose } from "react-icons/io5";

import {
  MdOutlinePersonOutline,
  MdOutlineCalendarMonth,
  MdOutlineEdit,
  MdOutlineDeleteOutline,
  MdOutlineChildCare,
  MdOutlineLightbulb,
  MdCheckCircle,
  MdAdd,
  MdOutlinePeopleAlt,
  MdFamilyRestroom,
} from "react-icons/md";
import { HiOutlineIdentification, HiOutlineUsers } from "react-icons/hi";
import { BsPersonFill, BsPersonHearts } from "react-icons/bs";
import { FaArrowRight } from "react-icons/fa";
import { TbBulb } from "react-icons/tb";

import Calendar from "react-calendar";

function CalendarField({ value, onChange, minDate }) {
  const [show, setShow] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (date) => {
    onChange(date.toISOString()); // store ISO
    setShow(false);
  };

  return (
    <div className=" w-full" ref={ref}>
      <input
        type="text"
        readOnly
        value={value ? new Date(value).toLocaleDateString() : ""}
        placeholder="Select date"
        onClick={() => setShow((prev) => !prev)}
        className="flex-1 text-sm outline-none bg-transparent text-gray-800 cursor-pointer w-full"
      />

      {show && (
        <div className="absolute z-50 mt-2 -translate-x-1/3  w-60">
          <Calendar
            onChange={handleSelect}
            value={value ? new Date(value) : null}
            minDate={minDate}
          />
        </div>
      )}
    </div>
  );
}
/* ─────────────────── helpers ─────────────────── */
const formatPassport = (v) =>
  v
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 9);

const isValidPassport = (v) => /^[A-Z]{2}[0-9]{7}$/.test(v);

const ROLE_LABELS = { spouse: "Spouse", child: "Child" };

const EMPTY_EXTRA = {
  role: "spouse",
  name: "",
  passport: "",
  expiry: "",
  dob: "",
};

/* ─────────────────── sub-components ─────────────────── */

function FieldInput({ label, icon: Icon, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-800 ">{label}</label>
      <div
        className={`flex items-center gap-2 outline rounded-lg! p-1 bg-white transition-colors ${
          error
            ? "outline-red-400"
            : "outline-gray-300 focus-within:outline-gray-400"
        }`}
      >
        {Icon && <Icon size={18} />}
        {children}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function MemberForm({ data, onChange, onSave, onCancel, showRolePicker }) {
  return (
    <div className="flex flex-col gap-3 pt-3 border-t border-gray-300 mt-1 ">
      {/* Role picker */}
      {showRolePicker && (
        <div className="flex gap-2">
          {["spouse", "child"].map((r) => (
            <button
              key={r}
              onClick={() => onChange("role", r)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl! outline text-xs! font-semibold transition-all ${
                data.role === r
                  ? "bg-amber-400 outline-amber-400 text-black"
                  : "bg-white outline-gray-400 text-gray-600 hover:outline-amber-300"
              }`}
            >
              {r === "spouse" ? (
                <BsPersonHearts size={14} />
              ) : (
                <MdOutlineChildCare size={15} />
              )}
              {ROLE_LABELS[r]}
            </button>
          ))}
        </div>
      )}

      {/* Full Name */}
      <FieldInput label="Full Name" icon={MdOutlinePersonOutline}>
        <input
          type="text"
          placeholder="Enter full name"
          value={data.name}
          onChange={(e) => onChange("name", e.target.value)}
          className="flex-1 text-sm! bg-transparent text-gray-800 placeholder-gray-400 min-w-0 outline-none"
        />
      </FieldInput>

      {/* Passport + Expiry */}
      <div className="grid grid-cols-2 gap-2">
        <FieldInput
          label="Passport No."
          icon={HiOutlineIdentification}
          error={
            data.passport && !isValidPassport(data.passport)
              ? "e.g. AB1234567"
              : ""
          }
        >
          <input
            type="text"
            placeholder="AB1234567"
            value={data.passport}
            maxLength={9}
            onChange={(e) =>
              onChange("passport", formatPassport(e.target.value))
            }
            className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400 min-w-0 w-full"
          />
        </FieldInput>

        <FieldInput label="Expiry Date">
          <CalendarField
            value={data.expiry}
            onChange={(val) => onChange("expiry", val)}
            minDate={new Date()}
          />
        </FieldInput>
      </div>

      {/* DOB */}
      <div className="w-1/2 pr-1">
        <FieldInput label="Date of Birth" icon={MdOutlineCalendarMonth}>
          <CalendarField
            value={data.dob}
            onChange={(val) => onChange("dob", val)}
          />
        </FieldInput>
      </div>

      {/* Buttons */}
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 py-1 outline outline-gray-400 text-sm font-medium text-gray-800 bg-white rounded-lg! transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={!data.name}
          className="flex-2 py-1  text-sm font-semibold text-black bg-yellow-400 rounded-lg! hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Save Member
        </button>
      </div>
    </div>
  );
}
function SelfForm({ data, onChange, formatCNIC }) {
  return (
    <div className="flex flex-col gap-3 pt-3 border-t border-gray-300 mt-1">
      {/* Full Name */}
      <FieldInput label="Full Name" icon={MdOutlinePersonOutline}>
        <input
          type="text"
          placeholder="Enter full name"
          value={data.name}
          onChange={(e) => onChange("name", e.target.value)}
          className="flex-1 text-sm bg-transparent text-gray-800 placeholder-gray-400 outline-none"
          readOnly
        />
      </FieldInput>

      {/* CNIC */}
      <FieldInput label="CNIC" icon={HiOutlineIdentification}>
        <input
          type="text"
          placeholder="xxxxx-xxxxxxx-x"
          value={data.cnic}
          onChange={(e) => onChange("cnic", formatCNIC(e.target.value))}
          className="flex-1 text-sm bg-transparent text-gray-800 placeholder-gray-400 outline-none"
        />
      </FieldInput>

      {/* Passport + Expiry */}
      <div className="grid grid-cols-2 gap-2">
        <FieldInput
          label="Passport No."
          icon={HiOutlineIdentification}
          error={
            data.passport && !isValidPassport(data.passport)
              ? "e.g. AB1234567"
              : ""
          }
        >
          <input
            type="text"
            placeholder="AB1234567"
            value={data.passport}
            maxLength={9}
            onChange={(e) =>
              onChange("passport", formatPassport(e.target.value))
            }
            className="flex-1 text-sm outline-none bg-transparent text-gray-800"
          />
        </FieldInput>

        <FieldInput label="Expiry Date" icon={MdOutlineCalendarMonth}>
          <CalendarField
            value={data.expiry}
            onChange={(val) => onChange("expiry", val)}
            minDate={new Date()}
          />
        </FieldInput>
      </div>

      {/* DOB */}
      <div className="w-1/2 pr-1">
        <FieldInput label="Date of Birth" icon={MdOutlineCalendarMonth}>
          <CalendarField
            value={data.dob}
            onChange={(val) => onChange("dob", val)}
          />
        </FieldInput>
      </div>
    </div>
  );
}
/* ─────────────────── main modal ─────────────────── */
const relations = [
  "Father",
  "Mother",
  "Spouse",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
];
export default function FamilyTravelerModal({
  isOpen,
  onClose,
  family,
  onSave,
  formatCNIC,
  validateCNIC,
  soloData,
  handleSoloChange,
}) {
  const [animate, setAnimate] = useState(false);
  const session = JSON.parse(sessionStorage.getItem("user"));
  /* extra members (up to 3) */
  const [extras, setExtras] = useState([]);

  /* which extra is being edited  (-1 = adding new) */
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState({ ...EMPTY_EXTRA });
  const [selfData, setSelfData] = useState({
    name: "",
    cnic: "",
    passport: "",
    expiry: "",
    dob: "",
  });
  useEffect(() => {
    if (isOpen) setTimeout(() => setAnimate(true), 10);
    else {
      setAnimate(false);
      setEditIdx(null);
    }
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      const mappedExtras = [];
      setSelfData({
        name: session?.userName,
        cnic: family?.self?.cnic || "",
        passport: family?.self?.passport || "",
      });
      if (family?.wife?.name) {
        mappedExtras.push({
          role: "spouse",
          name: family.wife.name,
          passport: family.wife.passport,
          expiry: "",
          dob: "",
        });
      }

      if (family?.kids?.length) {
        family.kids.forEach((k) => {
          if (k.name) {
            mappedExtras.push({
              role: "child",
              name: k.name,
              passport: k.passport,
              expiry: "",
              dob: "",
            });
          }
        });
      }

      setExtras(mappedExtras);
    }
  }, [isOpen, family]);
  if (!isOpen) return null;

  const canAdd = extras.length < 3;

  const openAdd = () => {
    setDraft({ ...EMPTY_EXTRA });
    setEditIdx(-1);
  };

  const openEdit = (i) => {
    setDraft({ ...extras[i] });
    setEditIdx(i);
  };

  const saveDraft = () => {
    if (editIdx === -1) {
      setExtras([...extras, { ...draft }]);
    } else {
      const updated = [...extras];
      updated[editIdx] = { ...draft };
      setExtras(updated);
    }
    setEditIdx(null);
  };

  const deleteExtra = (i) => {
    setExtras(extras.filter((_, idx) => idx !== i));
    if (editIdx === i) setEditIdx(null);
  };

  const handleSave = () => {
    onSave?.({
      self: selfData,
      extras,
    });
  };

  const totalAdded = 1 + extras.filter((e) => e.name).length;

  return (
    <div
      className={`fixed inset-0 z-50 px-2 flex items-center justify-center bg-black/70 transition-opacity duration-300  ${
        animate ? "opacity-100" : "opacity-0"
      }`}
      onClick={onClose}
    >
      {/* Modal Box */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full max-w-lg rounded-2xl shadow-lg p-3 relative transform transition-all duration-300 max-h-[90vh] overflow-y-auto ${
          animate
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 translate-y-4"
        }`}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-2  hover:text-black"
        >
          <IoClose className="text-gray-600 h-6 w-6" />
        </button>
        <div className="flex items-center justify-around ">
          <div className="font-normal text-black">
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
              Add Family Members
            </h4>
            <span className="text-xs! text-gray-600">
              Add each family member's passport details.
            </span>{" "}
          </div>
          <div className="w-18 h-18 bg-[#fef8ec]  rounded-full!">
            <img src="/passport.png" alt="Passport" className="w-full h-full" />
          </div>
        </div>
        <div className="px-4 sm:px-5 pt-2 sm:pt-3">
          {/* ── Info banner ── */}
          <div className="bg-[#fef8ec]  flex items-center gap-3 ring ring-[#fed40c] p-2 rounded-xl mb-3">
            <MdFamilyRestroom className="text-gray-800 bg-amber-200 p-2 rounded-full! h-13 w-12" />

            <div className="text-black flex-1 text-sm! h-13">
              <p className="text-xs! text-black leading-tight">
                You can add up to <strong>3 additional members</strong> — spouse
                or children. Self detail is mandatory.
              </p>
            </div>
          </div>
          {/* <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 mb-4">
            <MdOutlinePeopleAlt  size={20} className="shrink-0 mt-0.5" />
            <p className="text-xs! text-black leading-relaxed">
              You can add up to <strong>3 additional members</strong> — spouse
              or children. Self detail is mandatory.
            </p>
          </div> */}

          {/* ── Members count row ── */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-sm! font-bold text-gray-900 ">
                Family Members
              </span>
              <span className="bg-amber-200 text-gray-800 text-xs! font-bold px-2 py-0.5 rounded-full">
                {totalAdded} Added
              </span>
            </div>
            {canAdd && editIdx === null && (
              <button
                onClick={openAdd}
                className="flex items-center gap-1 text-xs! font-semibold text-gray-800 bg-amber-400 rounded-lg! px-2 py-1.5 hover:bg-amber-400 active:scale-95 transition-all"
              >
                <MdAdd size={15} />
                Add Member
              </button>
            )}
          </div>

          {/* ── Cards ── */}
          <div className="flex flex-col gap-3">
            {/* SELF — always locked / non-deletable */}
            <div className="outline outline-amber-300 rounded-2xl p-3 bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                  <BsPersonFill size={15} />
                </div>
                <div className="flex-1 min-w-0 items-start flex flex-col">
                  <p className="text-[9px] font-semibold text-center w-1/2 text-black bg-[#fed40c] p-0.5 rounded tracking-normal">
                    Self (Policy Holder)
                  </p>
                  <p className="text-sm font-bold text-gray-900 leading-0">
                    {" "}
                    {selfData.name || "—"}{" "}
                  </p>
                </div>
                <span className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 shrink-0">
                  <MdCheckCircle size={13} />
                  Added
                </span>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                <SelfForm
                  data={selfData}
                  onChange={(field, value) =>
                    setSelfData((prev) => ({ ...prev, [field]: value }))
                  }
                  formatCNIC={formatCNIC}
                />

                {/* <input
                  type="text"
                  placeholder="CNIC"
                  value={selfData.cnic}
                  onChange={(e) =>
                    setSelfData((prev) => ({
                      ...prev,
                      cnic: e.target.value,
                    }))
                  }
                  className="text-sm border rounded-lg p-1.5 outline-none"
                />

                <input
                  type="text"
                  placeholder="Passport"
                  value={selfData.passport}
                  onChange={(e) =>
                    setSelfData((prev) => ({
                      ...prev,
                      passport: formatPassport(e.target.value),
                    }))
                  }
                  className="text-sm border rounded-lg p-1.5 outline-none"
                /> */}
              </div>
            </div>

            {/* Extra members */}
            {extras.map((member, i) => {
              const isEditing = editIdx === i;
              return (
                <div
                  key={i}
                  className={`border rounded-2xl p-3 bg-white transition-all ${
                    isEditing
                      ? "border-amber-400 border-[1.5px]"
                      : "border-gray-200"
                  }`}
                >
                  <div className="">
                    <div className="flex items-center w-full justify-between px-3">
                      <div className="flex items-center justify-between w-1/2">
                        <span className="text-[9px] font-bold bg-amber-300 text-black rounded-lg! p-1 tracking-widest uppercase leading-none">
                          {ROLE_LABELS[member.role]}
                        </span>
                        {member.name && !isEditing && (
                          <span className="flex items-center gap-1 text-xs! font-semibold text-green-600 bg-green-50 border border-green-200 rounded-xl! px-2 py-0.5 shrink-0">
                            <MdCheckCircle size={13} />
                            Added
                          </span>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            onClick={() => openEdit(i)}
                            className="p-1.5 rounded-lg! bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors active:scale-95"
                          >
                            <MdOutlineEdit size={16} color="#6b7280" />
                          </button>
                          <button
                            onClick={() => deleteExtra(i)}
                            className="p-1.5 rounded-lg! bg-red-50 border border-red-100 hover:bg-red-100 transition-colors active:scale-95"
                          >
                            <MdOutlineDeleteOutline size={16} color="#ef4444" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {member.name || "—"}
                      </p>

                      {/* Extra member card — after member.name <p> */}
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {member.passport && (
                          <span className="text-[10px] text-gray-500">
                            <span className="font-semibold text-gray-600">
                              Passport:
                            </span>{" "}
                            {member.passport}
                          </span>
                        )}
                        {member.expiry && (
                          <span className="text-[10px] text-gray-500">
                            <span className="font-semibold text-gray-600">
                              Expiry:
                            </span>{" "}
                            {member.expiry}
                          </span>
                        )}
                        {member.dob && (
                          <span className="text-[10px] text-gray-500">
                            <span className="font-semibold text-gray-600">
                              DOB:
                            </span>{" "}
                            {member.dob}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {isEditing && (
                    <MemberForm
                      data={draft}
                      onChange={(field, val) =>
                        setDraft((d) => ({ ...d, [field]: val }))
                      }
                      onSave={saveDraft}
                      onCancel={() => setEditIdx(null)}
                      showRolePicker
                    />
                  )}
                </div>
              );
            })}

            {/* New member form */}
            {editIdx === -1 && (
              <div className="border-[1.5px] border-amber-400 rounded-2xl p-3 bg-[#fef8ec]  ">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-200  flex items-center justify-center shrink-0">
                    <MdFamilyRestroom size={17} />
                  </div>
                  <div className="">
                    <p className="text-[9px] font-semibold text-center text-black bg-[#fed40c] p-0.5 rounded tracking-normal">
                      New Member
                    </p>
                    <p className="text-sm font-bold text-black leading-0">
                      Select role below
                    </p>
                  </div>
                </div>
                <MemberForm
                  data={draft}
                  onChange={(field, val) =>
                    setDraft((d) => ({ ...d, [field]: val }))
                  }
                  onSave={saveDraft}
                  onCancel={() => setEditIdx(null)}
                  showRolePicker
                />
              </div>
            )}
          </div>

          {/* ── Max reached notice ── */}
          {!canAdd && editIdx === null && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mt-3">
              <MdFamilyRestroom size={18} color="#d97706" />
              <p className="text-xs text-amber-700 font-medium">
                Maximum 4 members reached (self + 3 others).
              </p>
            </div>
          )}

          {/* ── Tip ── */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 mt-3">
            <MdOutlineLightbulb size={17} color="#f59e0b" />
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">Tip:</span> Each
              additional member can be a spouse or a child.
            </p>
          </div>
          <div className=" p-3 bg-[#fef8ec]  rounded-xl! ring ring-[#fed40c] mt-3">
            <div className="flex items-center gap-3 rounded-xl pb-2">
              <HiOutlineUsers className="text-black h-12 w-12 bg-amber-200 p-2.5 rounded-full!" />

              <div className="text-black flex-1 h-12">
                <h6 className="font-bold!">Beneficary Details</h6>
                <p className="text-xs! text-gray-700">
                  A person who will be notified in case of emergency or claim.
                </p>
              </div>
            </div>
            <div className="my-2">
              <label className="text-sm! font-semibold">
                Beneficiary Full Name
              </label>
              <input
                type="text"
                value={soloData.beneficiaryName}
                placeholder="Enter Full name"
                onChange={(e) =>
                  handleSoloChange("beneficiaryName", e.target.value)
                }
                className="w-full bg-white p-2 text-xs! rounded-lg outline outline-amber-500 focus-visible:outline-amber-500!"
              />
            </div>
            <div className="mb-2 ">
              <label className="text-sm! font-semibold mb-1">Relation</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {relations.map((rel) => (
                  <button
                    key={rel}
                    type="button"
                    onClick={() => handleSoloChange("relation", rel)}
                    className={`px-3 py-1 rounded-full! text-xs! border transition ${
                      soloData.relation === rel
                        ? "bg-black text-yellow-400 border-black"
                        : "bg-white text-black border-gray-300"
                    }`}
                  >
                    {rel}
                  </button>
                ))}
              </div>
            </div>
            {/* CNIC */}
            <div className="mb-2">
              <label className="text-xs! font-semibold">CNIC</label>
              <input
                type="text"
                value={soloData.cnic}
                placeholder="Enter 13-digit CNIC"
                onChange={(e) =>
                  handleSoloChange("cnic", formatCNIC(e.target.value))
                }
                className={`w-full bg-white p-2 text-xs! border  focus-visible:outline-none focus-visible:border-amber-500! rounded-lg ${
                  soloData.cnic && !validateCNIC(soloData.cnic)
                    ? "border-red-500"
                    : "border-amber-500"
                }`}
              />

              {soloData.cnic && !validateCNIC(soloData.cnic) && (
                <p className="text-red-500 text-xs! mt-1">
                  Invalid CNIC format
                </p>
              )}
            </div>

            {/* Relation */}

            <div className="mt-2 text-xs! bg-yellow-100  px-3 py-2 rounded-lg text-black">
              <TbBulb className="inline-block mr-1 bg-amber-200 w-8 h-8 text-black rounded-full! p-1" />
              <strong>Tip:</strong> Accurate beneficiary details help process
              your claim faster.
            </div>
          </div>
          {/* ── Footer ── */}
          <div className="flex gap-3 mt-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm! font-semibold text-gray-800 bg-white outline outline-gray-500 rounded-2xl! hover:bg-gray-50 active:scale-95 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-2 py-3 text-sm! font-bold text-black bg-amber-400 hover:bg-amber-500 active:scale-95 rounded-xl! flex items-center justify-center gap-2 transition-all"
            >
              Save Details
              <FaArrowRight className="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
